import { supabase } from '@/integrations/supabase/client';

interface CloudinaryUploadResult {
  secure_url: string;
  public_id: string;
  width: number;
  height: number;
}

export async function uploadToCloudinary(
  file: Blob, 
  publicId: string
): Promise<CloudinaryUploadResult | null> {
  try {
    console.log('🔄 [Cloudinary] Starting upload process for:', publicId);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('public_id', publicId);
    formData.append('folder', 'collections/markup');
    
    const timestamp = Math.round(Date.now() / 1000);
    formData.append('timestamp', String(timestamp));
    
    // Create signature for secure upload
    const paramsToSign = {
      public_id: publicId,
      folder: 'collections/markup',
      timestamp: String(timestamp),
    };
    
    const signatureStr = Object.entries(paramsToSign)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    
    console.log('🔐 [Cloudinary] Getting signature from Supabase function...');
    
    // Use Supabase function to generate the signature
    const signatureResponse = await supabase.functions.invoke('cloudinary-signature', {
      body: { paramsToSign: signatureStr }
    });
    
    if (signatureResponse.error) {
      console.error('❌ [Cloudinary] Signature generation failed:', signatureResponse.error);
      throw new Error('Failed to get signature: ' + signatureResponse.error.message);
    }
    
    const { signature, apiKey, cloudName } = signatureResponse.data;
    
    console.log('✅ [Cloudinary] Signature received, uploading to Cloudinary...');
    
    formData.append('signature', signature);
    formData.append('api_key', apiKey);
    
    const uploadResponse = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );
    
    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('❌ [Cloudinary] Upload failed:', errorText);
      throw new Error(`Upload failed: ${uploadResponse.status} - ${errorText}`);
    }
    
    const result = await uploadResponse.json();
    
    console.log('✅ [Cloudinary] Upload successful:', result.secure_url);
    
    return {
      secure_url: result.secure_url,
      public_id: result.public_id,
      width: result.width,
      height: result.height,
    };
  } catch (error) {
    console.error('❌ [Cloudinary] Error uploading:', error);
    return null;
  }
}

/**
 * Strips Cloudinary transformations and overlays/annotations from a URL to get the original image URL.
 * Handles URLs like:
 *   - https://res.cloudinary.com/{cloud}/image/upload/c_crop,x_0,y_0,w_100,h_100/{public_id}
 *   - https://res.cloudinary.com/{cloud}/image/upload/l_one_pixel_gidbyp,w_100,h_100,fl_layer_apply/{public_id}
 * Returns: https://res.cloudinary.com/{cloud}/image/upload/{public_id}
 */
export function stripCloudinaryTransformations(url: string | null | undefined): string | null {
  if (!url) return null;

  // Check if it's a Cloudinary URL - match the base URL and everything after /upload/
  const cloudinaryPattern = /^(https?:\/\/res\.cloudinary\.com\/[^\/]+\/image\/upload\/)(.+)$/;
  const match = url.match(cloudinaryPattern);

  if (!match) {
    // Not a Cloudinary URL, return as-is
    return url;
  }

  const [, baseUrl, afterUpload] = match;

  // Split by slash to separate transformation chains and the public ID path
  const parts = afterUpload.split('/');

  // Find where the public ID starts (the part that doesn't contain transformation syntax)
  // Transformations contain underscores or commas: c_crop,x_0 or f_auto
  // Version numbers look like: v1234567890
  // Public IDs and folders typically don't have transformation syntax
  let publicIdStartIndex = parts.findIndex(part => {
    // Check if this part looks like a transformation
    const hasTransformationSyntax = /[a-z]_/.test(part) || part.includes(',');

    // Check if it's a version number (v followed by digits)
    const isVersion = /^v\d+$/.test(part);

    // If it has transformation syntax and is not a version, it's a transformation
    return !hasTransformationSyntax || isVersion;
  });

  // If no public ID found (all parts are transformations), the last part is likely the file name
  if (publicIdStartIndex === -1) {
    publicIdStartIndex = parts.length - 1;
  }

  // Extract the public ID path (everything from publicIdStartIndex onwards)
  const publicIdParts = parts.slice(publicIdStartIndex);

  // If we removed some parts (transformations), return the cleaned URL
  if (publicIdStartIndex > 0) {
    return `${baseUrl}${publicIdParts.join('/')}`;
  }

  // No transformations found, return as-is
  return url;
}